import { types, flow, cast } from "mobx-state-tree"
import { inboundDeliveryBusiness } from "businesses/inbound-delivery-business"
import format from "date-fns/format"

const InboundDeliveryStore = types
  .model({
    data: types.optional(types.frozen(), []),
    deliveryInfo: types.maybeNull(types.frozen()),
    products: types.optional(types.frozen(), []),
    isLoading: types.maybeNull(types.boolean),
    customFilters: types.maybeNull(types.frozen()),
    selectedId: types.maybeNull(types.string, ""),
    selectedReceiptId: types.maybeNull(types.string, ""),
    searchText: types.maybeNull(types.string, ""),
    forceReload: types.maybeNull(types.boolean, false),
  })
  .views(self => ({
    get filters() {
      const res = {}
      if (self.customFilters) {
        const dateItems = ["before_date", "after_date"]
        for (const prop in self.customFilters) {
          const val = self.customFilters[prop]
          if (val) {
            if (dateItems.includes(prop)) {
              res[prop] = format(val, "yyyy-MM-dd")
            } else {
              res[prop] = val
            }
          }
        }
      }

      return res
    },
    get isArchivalSearch() {
      return self.customFilters?.archival_search || false
    },
  }))
  .actions(self => ({
    setForceReload() {
      self.forceReload = !self.forceReload
    },
    setData(data) {
      self.data = data
    },
    setSelectedId(id) {
      self.selectedId = id
    },
    setSearchText(val) {
      self.searchText = val
    },
    setCustomFilters(data) {
      self.customFilters = cast(data)
    },
  }))
  .actions(self => ({
    resetData() {
      self.deliveryInfo = null
      self.selectedReceiptId = ""
      self.selectedId = ""
    },
    loadData: flow(function* (showLoading = true) {
      if (!self.searchText) {
        self.setData([])
        return
      }

      if (showLoading) self.isLoading = true
      const searchParams = {
        ...(self.filters || {}),
      }
      const data = yield inboundDeliveryBusiness.getData(searchParams)
      self.setData(data)
      if (showLoading) self.isLoading = false
    }),
    loadDetailData: flow(function* (receiptId, id) {
      self.selectedId = id
      self.selectedReceiptId = receiptId

      self.isLoading = true
      const searchParams = {
        receipt_id: decodeURIComponent(receiptId),
        id,
        archival_search: self.isArchivalSearch,
      }
      const deliveryInfo = yield inboundDeliveryBusiness.getDeliveryInformation(searchParams)
      self.deliveryInfo = cast(deliveryInfo)

      const products = yield inboundDeliveryBusiness.getProductInformation(searchParams)

      self.products = cast(products)

      self.isLoading = false
    }),
    addOrUpdateData: flow(function* (data) {
      const result = self.selectedId ? yield inboundDeliveryBusiness.updateData(data) : yield inboundDeliveryBusiness.addData(data)
      return result
    }),
    deleteData: flow(function* () {
      self.isLoading = true
      const params = {
        receipt_id: self.selectedReceiptId,
        id: self.selectedId,
      }
      const result = yield inboundDeliveryBusiness.deleteData(params)
      self.isLoading = false

      // The API answers with a list: [{ STATUS_CODE: "2040" (orders) / "2041" (receipts), STATUS_NAME: "DELETION_SUCCESS", ... }]
      const status = Array.isArray(result) ? result[0] : result
      return status?.STATUS_NAME === "DELETION_SUCCESS"
    }),
    uploadData: flow(function* (data) {
      return yield inboundDeliveryBusiness.uploadData(data)
    }),
  }))
export default InboundDeliveryStore
